import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { LogOut, User, BookOpen, ClipboardCheck, DollarSign, Bell, Calendar } from 'lucide-react';

export default function StudentPortal() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [notices, setNotices] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    api.get('/notices').then(r => setNotices(r.data?.slice(0, 5) || [])).catch(() => {});
    api.get('/holidays').then(r => setHolidays(r.data?.filter(h => new Date(h.date) >= new Date()).slice(0, 5) || [])).catch(() => {});
  }, []);

  const initials = user?.full_name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', fontFamily: 'Poppins, sans-serif' }}>
      {/* Topbar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e7ff', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 4px rgba(26,115,232,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#1a73e8,#0f9d58)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" fill="#fff" width="20" height="20"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zm0 12.5L3 11.1V15c0 2.8 4 5 9 5s9-2.2 9-5v-3.9l-9 4.4z"/></svg>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>School Portal</div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Student / Parent Access</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#e8f0fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#1a73e8', fontSize: '0.8rem' }}>
              {user?.photo_url ? <img src={user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : initials}
            </div>
            <div>
              <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{user?.full_name}</div>
              <div style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'capitalize' }}>{user?.role}</div>
            </div>
          </div>
          <button onClick={logout} style={{ background: '#fce8e6', color: '#d93025', border: 'none', borderRadius: 8, padding: '7px 14px', fontFamily: 'Poppins', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 20px' }}>
        {/* Welcome */}
        <div style={{ background: 'linear-gradient(135deg,#1a73e8,#0f9d58)', borderRadius: 20, padding: '28px 32px', color: '#fff', marginBottom: 28 }}>
          <div style={{ fontSize: '0.82rem', opacity: 0.8, marginBottom: 6 }}>Welcome back,</div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 8 }}>{user?.full_name}</h1>
          <div style={{ opacity: 0.85, fontSize: '0.9rem' }}>
            {user?.role === 'student' ? 'Track your attendance, results, and school updates here.' : 'Monitor your ward\'s academic progress and school updates.'}
          </div>
        </div>

        {/* Info banner */}
        <div style={{ background: '#fef3e2', border: '1px solid #f8c76e', borderRadius: 14, padding: '16px 20px', marginBottom: 28, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
          <div>
            <div style={{ fontWeight: 700, color: '#b35c00', marginBottom: 4 }}>Limited Portal Access</div>
            <div style={{ fontSize: '0.85rem', color: '#8a4a00', lineHeight: 1.6 }}>
              This is the basic school portal. For full academic details — attendance, results, fee receipts — please contact your class teacher or the school office. Your teacher has access to the complete ERP system.
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e0e7ff', marginBottom: 24 }}>
          {[
            { key: 'overview', label: 'Overview', icon: <User size={15}/> },
            { key: 'notices', label: 'Notices', icon: <Bell size={15}/> },
            { key: 'holidays', label: 'Holidays', icon: <Calendar size={15}/> },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', background: 'transparent', fontFamily: 'Poppins', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2, borderRadius: '8px 8px 0 0', color: tab === t.key ? '#1a73e8' : '#6b7280', borderBottomColor: tab === t.key ? '#1a73e8' : 'transparent', transition: 'all 0.15s' }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { icon: <ClipboardCheck size={24}/>, label: 'Attendance', desc: 'View your attendance record', color: '#e8f0fe', iconColor: '#1a73e8', action: 'Contact Class Teacher' },
              { icon: <BookOpen size={24}/>, label: 'Exam Results', desc: 'View your marks and grades', color: '#e6f4ea', iconColor: '#0f9d58', action: 'Visit School Office' },
              { icon: <DollarSign size={24}/>, label: 'Fee Status', desc: 'Check your fee payment status', color: '#fef3e2', iconColor: '#f29900', action: 'Visit Accounts Office' },
              { icon: <User size={24}/>, label: 'My Profile', desc: 'Your school profile information', color: '#ede7f6', iconColor: '#7c4dff', action: 'Update via Admin' },
            ].map(card => (
              <div key={card.label} style={{ background: '#fff', borderRadius: 16, padding: '22px', border: '1px solid #e0e7ff', boxShadow: '0 2px 10px rgba(26,115,232,0.06)' }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.iconColor, marginBottom: 14 }}>
                  {card.icon}
                </div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{card.label}</div>
                <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>{card.desc}</div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: card.iconColor, background: card.color, padding: '4px 10px', borderRadius: 999, display: 'inline-block' }}>{card.action}</div>
              </div>
            ))}
          </div>
        )}

        {/* Notices tab */}
        {tab === 'notices' && (
          <div>
            {notices.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontWeight: 600 }}>No active notices</div>}
            {notices.map((n, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '18px 22px', marginBottom: 14, border: '1px solid #e0e7ff', borderLeft: `4px solid ${n.priority === 'urgent' ? '#d93025' : n.priority === 'high' ? '#f29900' : '#1a73e8'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{n.title}</div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 999, background: n.priority === 'urgent' ? '#fce8e6' : '#e8f0fe', color: n.priority === 'urgent' ? '#d93025' : '#1a73e8' }}>{n.priority}</span>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.6 }}>{n.content}</div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 10 }}>Posted by {n.published_by_name} · {new Date(n.created_at).toLocaleDateString('en-IN')}</div>
              </div>
            ))}
          </div>
        )}

        {/* Holidays tab */}
        {tab === 'holidays' && (
          <div>
            {holidays.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontWeight: 600 }}>No upcoming holidays</div>}
            {holidays.map((h, i) => {
              const typeColor = { national: '#1a73e8', school: '#0f9d58', regional: '#7c4dff', event: '#f29900' };
              return (
                <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '16px 22px', marginBottom: 12, border: '1px solid #e0e7ff', display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 12, background: '#f0f4ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a73e8', lineHeight: 1 }}>{new Date(h.date).getDate()}</div>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: 700 }}>{new Date(h.date).toLocaleString('en-IN', { month: 'short' })}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{h.name}</div>
                    {h.description && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 3 }}>{h.description}</div>}
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize', padding: '4px 10px', borderRadius: 999, background: '#f0f4ff', color: typeColor[h.type] || '#1a73e8' }}>{h.type}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
